/**
 * Header used only on /preview/photos. Live Header.tsx is not edited.
 * Home and the logo go to the live homepage.
 */

import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Bars3Icon, ShoppingCartIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useCart } from '../../context/CartContext';
import { useSiteImageOverrides } from '../../hooks/useSiteImageOverrides';

const navigation = [
  { name: 'Home', href: '/' },
  { name: 'Menu', href: '/menu' },
  { name: 'Gallery', href: '/gallery' },
  { name: 'About', href: '/about' },
  { name: 'Contact', href: '/contact' },
];

export default function PreviewHeader() {
  const { getImage } = useSiteImageOverrides();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { totalItems, totalPrice, setCartOpen } = useCart();

  const handleCartClick = () => {
    if (location.pathname === '/order') {
      setCartOpen(true);
    } else {
      navigate('/order');
      setTimeout(() => setCartOpen(true), 100);
    }
  };

  return (
    <header className="sticky top-0 z-40 border-b border-primary-100/80 bg-white/90 backdrop-blur-md">
      <nav className="container-custom flex h-20 items-center justify-between">
        <Link to="/" className="flex items-center gap-3">
          <img
            src={getImage('logo', '/assets/images/branding/logo2.webp')}
            alt="Nanny B's Bakery logo"
            className="h-12 w-auto"
          />
          <div className="hidden sm:block">
            <p className="font-display text-xl font-bold text-primary-800">Nanny B&apos;s Bakery</p>
            <p className="text-xs tracking-wide text-gray-500">Barrie, Ontario</p>
          </div>
        </Link>

        <div className="hidden items-center gap-6 md:flex">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              className="text-sm font-medium text-gray-700 hover:text-primary-700"
            >
              {item.name}
            </Link>
          ))}
          <button
            type="button"
            onClick={handleCartClick}
            className="inline-flex items-center gap-2 rounded-full bg-primary-700 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-primary-800"
            aria-label="View cart"
          >
            <ShoppingCartIcon className="h-5 w-5" />
            {totalItems > 0 ? `$${totalPrice.toFixed(2)}` : 'Cart'}
          </button>
        </div>

        <div className="flex items-center gap-2 md:hidden">
          <button
            type="button"
            onClick={handleCartClick}
            className="rounded-full bg-primary-700 p-2 text-white"
            aria-label="View cart"
          >
            <ShoppingCartIcon className="h-5 w-5" />
          </button>
          <button
            type="button"
            className="p-2 text-gray-700"
            onClick={() => setMobileMenuOpen((open) => !open)}
            aria-label={mobileMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
          >
            {mobileMenuOpen ? <XMarkIcon className="h-6 w-6" /> : <Bars3Icon className="h-6 w-6" />}
          </button>
        </div>
      </nav>

      {mobileMenuOpen && (
        <div className="border-t border-gray-100 px-4 py-3 md:hidden">
          {navigation.map((item) => (
            <Link
              key={item.name}
              to={item.href}
              onClick={() => setMobileMenuOpen(false)}
              className="block rounded-md px-3 py-2 text-gray-800 hover:bg-bakery-cream"
            >
              {item.name}
            </Link>
          ))}
        </div>
      )}
    </header>
  );
}
